/**
 * Utilitaire pour tester la connexion au backend
 * Utilisez ce fichier pour vérifier que tout fonctionne correctement
 */

import api from '../api/client';
import socketService from '../api/socket';
import {
  authService,
  agentService,
  conversationService,
  productService,
  notificationService,
  analyticsService
} from '../api/services';

/**
 * Tester la connexion de base à l'API
 */
export const testApiConnection = async () => {
  console.log('🔍 Test de connexion à l\'API...');
  
  try {
    const response = await api.get('/health');
    console.log('✅ API connectée avec succès !', response.data);
    return true;
  } catch (error) {
    console.error('❌ Erreur de connexion à l\'API:', error.message);
    if (error.response) {
      console.error('Détails:', error.response.data);
    } else if (error.request) {
      console.error('Aucune réponse du serveur. Vérifiez que le backend est démarré.');
    }
    return false;
  }
};

/**
 * Tester l'authentification
 */
export const testAuthentication = async (email, password) => {
  console.log('🔍 Test d\'authentification...');
  
  try {
    const response = await authService.login({ email, password });
    console.log('✅ Authentification réussie !', response.data);
    return true;
  } catch (error) {
    console.error('❌ Erreur d\'authentification:', error.message);
    return false;
  }
};

/**
 * Tester la connexion WebSocket
 */
export const testWebSocket = () => {
  console.log('🔍 Test de connexion WebSocket...');
  
  const token = localStorage.getItem('accessToken');
  
  if (!token) {
    console.error('❌ Aucun token trouvé. Connectez-vous d\'abord.');
    return false;
  }

  try {
    socketService.connect(token);
    
    // Vérifier la connexion après un court délai
    setTimeout(() => {
      if (socketService.isConnected()) {
        console.log('✅ WebSocket connecté avec succès !');
        
        // Tester l'écoute d'événements
        socketService.on('notification', (data) => {
          console.log('📬 Notification reçue:', data);
        });
        
        console.log('📡 En écoute des notifications...');
      } else {
        console.error('❌ WebSocket non connecté');
      }
    }, 2000);
    
    return true;
  } catch (error) {
    console.error('❌ Erreur WebSocket:', error.message);
    return false;
  }
};

/**
 * Tester les services principaux
 */
export const testServices = async () => {
  console.log('🔍 Test des services...');
  
  const results = {
    agents: false,
    conversations: false,
    products: false,
    notifications: false,
    analytics: false
  };

  // Test Agent Service
  try {
    const agentsResponse = await agentService.getAgents({ page: 1, limit: 5 });
    console.log('✅ Agent Service OK:', agentsResponse.data);
    results.agents = true;
  } catch (error) {
    console.error('❌ Agent Service erreur:', error.message);
  }

  // Test Conversation Service
  try {
    const conversationsResponse = await conversationService.getConversations({ page: 1, limit: 5 });
    console.log('✅ Conversation Service OK:', conversationsResponse.data);
    results.conversations = true;
  } catch (error) {
    console.error('❌ Conversation Service erreur:', error.message);
  }

  // Test Product Service
  try {
    const productsResponse = await productService.getProducts({ page: 1, limit: 5 });
    console.log('✅ Product Service OK:', productsResponse.data);
    results.products = true;
  } catch (error) {
    console.error('❌ Product Service erreur:', error.message);
  }

  // Test Notification Service
  try {
    const notificationsResponse = await notificationService.getNotifications({ page: 1, limit: 5 });
    console.log('✅ Notification Service OK:', notificationsResponse.data);
    results.notifications = true;
  } catch (error) {
    console.error('❌ Notification Service erreur:', error.message);
  }

  // Test Analytics Service
  try {
    const analyticsResponse = await analyticsService.getDashboardStats();
    console.log('✅ Analytics Service OK:', analyticsResponse.data);
    results.analytics = true;
  } catch (error) {
    console.error('❌ Analytics Service erreur:', error.message);
  }

  // Résumé
  const successCount = Object.values(results).filter(Boolean).length;
  const totalCount = Object.keys(results).length;
  
  console.log(`\n📊 Résultat: ${successCount}/${totalCount} services fonctionnels`);
  console.log('Détails:', results);
  
  return results;
};

/**
 * Exécuter tous les tests
 */
export const runAllTests = async (credentials = null) => {
  console.log('🚀 Démarrage des tests de connexion...\n');
  
  // 1. Test de connexion API
  const apiConnected = await testApiConnection();
  
  if (!apiConnected) {
    console.log('\n⚠️ Impossible de continuer sans connexion API.');
    console.log('Vérifiez que le backend est démarré sur http://localhost:5000');
    return;
  }

  // 2. Test d'authentification (si credentials fournis)
  if (credentials) {
    const authenticated = await testAuthentication(credentials.email, credentials.password);
    
    if (!authenticated) {
      console.log('\n⚠️ Authentification échouée. Certains tests seront ignorés.');
    } else {
      // 3. Test WebSocket
      testWebSocket();
      
      // 4. Test des services (nécessite authentification)
      setTimeout(async () => {
        await testServices();
        
        console.log('\n✅ Tests terminés !');
      }, 3000);
    }
  } else {
    console.log('\n⚠️ Aucun identifiant fourni. Tests d\'authentification ignorés.');
    console.log('Pour tester l\'authentification, appelez:');
    console.log('runAllTests({ email: "user@example.com", password: "password" })');
  }
};

/**
 * Fonction d'aide pour afficher les informations de connexion
 */
export const showConnectionInfo = () => {
  console.log('📋 Informations de connexion:');
  console.log('API URL:', process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1');
  console.log('WebSocket URL:', process.env.REACT_APP_WS_URL || 'http://localhost:5000');
  console.log('Token présent:', !!localStorage.getItem('accessToken'));
  console.log('Utilisateur:', localStorage.getItem('user') || 'Non connecté');
};

// Export par défaut
export default {
  testApiConnection,
  testAuthentication,
  testWebSocket,
  testServices,
  runAllTests,
  showConnectionInfo
};
